/**
 * Application Configuration
 *
 * Centralized configuration with environment variable support.
 * For production, these can be set via Vite's define or envPrefix.
 *
 * Environment variables (set in .env or at build time):
 * - VITE_API_URL: Base URL for the API (e.g., "https://api-os.pharosfabric.ai")
 * - VITE_WS_URL: WebSocket URL for subscriptions (derived from API_URL if not set)
 */

/**
 * Determine if we're in production mode
 */
export const isProduction = import.meta.env.PROD;

/**
 * API base URL
 * - In development: Uses Vite proxy (relative path) - proxy forwards to VITE_API_URL
 * - In production: Uses VITE_API_URL or derives from current host
 */
export function getApiUrl(): string {
  // In development, always use relative path (Vite proxy handles forwarding)
  if (!isProduction) {
    return '';
  }

  // Explicit environment variable takes precedence in production
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // In production, derive from current location
  // e.g., os.pharosfabric.ai -> api-os.pharosfabric.ai
  const { protocol, hostname } = window.location;

  // Replace first subdomain segment with 'api-{segment}'
  // os.pharosfabric.ai -> api-os.pharosfabric.ai
  // studio.pharosfabric.ai -> api-studio.pharosfabric.ai
  // (Legacy *.imaginariax.com hosts never get here — main.tsx redirects them
  // to their pharos twin first; see getLegacyHostRedirect.)
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    parts[0] = `api-${parts[0]}`;
  }
  return `${protocol}//${parts.join('.')}`;
}

/**
 * Retired app hosts and their pharosfabric.ai twins (CDEV-2228). The same
 * Pages build is still served on the legacy domain until its hostnames are
 * dropped, but the API no longer allows those origins (CORS), so the SPA must
 * leave before it makes a single API call.
 */
const LEGACY_APP_HOSTS: Record<string, string> = {
  'os.imaginariax.com': 'os.pharosfabric.ai',
  'studio.imaginariax.com': 'studio.pharosfabric.ai',
};

/**
 * Where to send a browser that loaded the SPA on a retired host, or null when
 * the current host is not retired. Path, query and hash are kept, so old
 * bookmarks and already-minted invite links land on the same route.
 */
export function getLegacyHostRedirect(location: {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const twin = LEGACY_APP_HOSTS[location.hostname.toLowerCase()];
  if (!twin) return null;
  return `https://${twin}${location.pathname}${location.search}${location.hash}`;
}

/**
 * API base URL for REST endpoints (e.g., /api/invite/)
 * Returns the base URL without trailing slash.
 */
export function getApiBaseUrl(): string {
  return getApiUrl();
}

/**
 * GraphQL endpoint URL
 */
export function getGraphqlUrl(): string {
  const base = getApiUrl();
  return base ? `${base}/query` : '/graphql';
}

/**
 * WebSocket URL for GraphQL subscriptions
 */
export function getSubscriptionUrl(): string {
  // Explicit environment variable
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  const apiUrl = getApiUrl();

  if (!apiUrl) {
    // Development: use current host with ws protocol
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}/subscriptions`;
  }

  // Production: derive from API URL
  const url = new URL(apiUrl);
  const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${url.host}/subscriptions`;
}

/**
 * Dev-only session-key auth (CDEV-404).
 *
 * When VITE_DEV_SESSION_KEY is set in a development build, ix-studio
 * authenticates to the fabric with a bare session keyname
 * (`Authorization: <keyname>`, no Bearer/JWT/OIDC) instead of the interactive
 * OIDC authorization-code flow — so automated agents can verify the app
 * locally without the IdP redirect. The keyname resolves to a service
 * principal server-side via NESC_SESSION_KEYS.
 *
 * HARD-gated on import.meta.env.DEV so it can never be active in a prod build,
 * even if the env var is present.
 */
export function getDevSessionKey(): string | undefined {
  if (!import.meta.env.DEV) return undefined;
  const key = import.meta.env.VITE_DEV_SESSION_KEY;
  return key && key.length > 0 ? key : undefined;
}

/** True when dev session-key auth is active (see {@link getDevSessionKey}). */
export function isDevSessionAuth(): boolean {
  return getDevSessionKey() !== undefined;
}

/**
 * Control ledger name for Fluree queries
 */
export function getControlLedger(): string {
  return import.meta.env.VITE_CONTROL_LEDGER || 'ixos-control';
}

/**
 * OIDC issuer authority URL.
 *
 * Defaults to the Pharos Authentik issuer (CDEV-379); the legacy
 * auth.dev.imaginariax.com host is being retired. VITE_OIDC_AUTHORITY overrides.
 */
export function getOidcAuthority(): string {
  return (
    import.meta.env.VITE_OIDC_AUTHORITY ||
    'https://auth.pharosfabric.ai/application/o/ix-fabric-api/'
  );
}

/**
 * Protocol claims oidc-client-ts removes from the user's profile. Its default
 * list also removes auth_time, which the address change and passkey binding
 * read to tell whether a sign-in is fresh; without it no sign-in ever looks
 * fresh (CDEV-2455). This is that default list, less auth_time.
 */
export const OIDC_FILTERED_PROTOCOL_CLAIMS = ['nbf', 'jti', 'nonce', 'acr', 'amr', 'azp', 'at_hash'];

/**
 * signinRedirect arguments that make Authentik ask for a new sign-in. Not
 * prompt=login: Authentik 2026.5 honours that once per session (it stores the
 * login's id and never clears it, so a later one returns the old auth_time
 * without asking; CDEV-2455). max_age compares the login's real age. 60 seconds
 * is far below the 14 minutes after which Studio asks, and far above the
 * redirect back from the sign-in, which is checked against it too.
 */
export const OIDC_FRESH_SIGNIN = { max_age: 60 };

/**
 * OIDC client_id. Override with VITE_OIDC_CLIENT_ID.
 */
export function getOidcClientId(): string {
  return import.meta.env.VITE_OIDC_CLIENT_ID || 'ix-fabric-api';
}

/**
 * Authentik OAuth2 token endpoint.
 *
 * Authentik serves the token endpoint one path segment up from the per-application
 * issuer: ".../application/o/<app>/" -> ".../application/o/token/". Derived from the
 * authority so it tracks the cutover automatically; override with
 * VITE_OIDC_TOKEN_ENDPOINT if a non-standard layout is ever needed.
 */
export function getOidcTokenEndpoint(): string {
  if (import.meta.env.VITE_OIDC_TOKEN_ENDPOINT) {
    return import.meta.env.VITE_OIDC_TOKEN_ENDPOINT;
  }
  const authority = getOidcAuthority().replace(/\/+$/, '');
  const providerRoot = authority.replace(/\/[^/]+$/, '');
  return `${providerRoot}/token/`;
}

/**
 * localStorage key under which react-oidc-context persists the user:
 * `oidc.user:<authority>:<client_id>`. Computed from the same source as the
 * provider config so the eager token read in auth.ts stays in sync when the
 * authority hostname changes during the cutover (authentik skill Gotcha #8).
 */
export function getOidcStorageKey(): string {
  return `oidc.user:${getOidcAuthority()}:${getOidcClientId()}`;
}

/**
 * Default polling interval for live queries (ms)
 */
export const DEFAULT_POLL_INTERVAL = 5000;

/**
 * Subscription reconnect settings
 */
export const SUBSCRIPTION_CONFIG = {
  retryAttempts: 5,
  retryWait: 3000,
};

/**
 * Application Mode
 *
 * The same application serves different entry points with different scopes:
 * - "os": General agentic platform (os.pharosfabric.ai)
 * - "studio": Content creation platform (studio.pharosfabric.ai)
 */
export type AppMode = 'os' | 'studio';

/**
 * Detect the app mode based on hostname
 */
export function getAppMode(): AppMode {
  // Allow override via environment variable for development
  if (import.meta.env.VITE_APP_MODE) {
    return import.meta.env.VITE_APP_MODE as AppMode;
  }

  const { hostname } = window.location;

  // Production detection based on subdomain
  if (hostname.startsWith('os.')) {
    return 'os';
  }
  if (hostname.startsWith('studio.')) {
    return 'studio';
  }

  // Default to os for local development and unknown hosts
  return 'os';
}

/**
 * Check if running in OS mode
 */
export function isOsMode(): boolean {
  return getAppMode() === 'os';
}

/**
 * Check if running in Studio mode
 */
export function isStudioMode(): boolean {
  return getAppMode() === 'studio';
}

/** localStorage key that turns the A2UI form path on for this browser. */
export const A2UI_FORMS_STORAGE_KEY = 'ix-studio:a2ui-forms';

/**
 * Whether GENUI forms render through the A2UI path (MEMFAB-A2UI-SPEC,
 * CDEV-2053) instead of the native AffordanceRenderer. On when the build sets
 * VITE_A2UI_FORMS=true, or for one browser with
 * localStorage['ix-studio:a2ui-forms'] = '1'. Off by default.
 */
export function isA2UIFormsEnabled(): boolean {
  if (import.meta.env.VITE_A2UI_FORMS === 'true') return true;
  try {
    return globalThis.localStorage?.getItem(A2UI_FORMS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Whether the invite page offers "Use my institutional email" (CDEV-2395). The
 * button needs Authentik's invitation-only email enrollment
 * (infra/IaC/authentik/blueprints/40-email-passkey-signin.yaml); until that is
 * applied, Authentik ignores the request and shows social sign-in. Off unless
 * the build sets VITE_INSTITUTIONAL_EMAIL_SIGNIN=true.
 */
export function isInstitutionalEmailSignInEnabled(): boolean {
  return import.meta.env.VITE_INSTITUTIONAL_EMAIL_SIGNIN === 'true';
}

/**
 * Whether Settings offers "Add a passkey" (CDEV-2432). It needs Authentik's
 * pharos-passkey-binding flow (infra/IaC/authentik/blueprints/41-passkey-binding.yaml);
 * off unless the build sets VITE_PASSKEY_BINDING=true.
 */
export function isPasskeyBindingEnabled(): boolean {
  return import.meta.env.VITE_PASSKEY_BINDING === 'true';
}

/**
 * Whether Sign-in offers "Change sign-in address" (CDEV-2455). It needs
 * Authentik's pharos-address-change flow
 * (infra/IaC/authentik/blueprints/43-address-change.yaml); off unless the
 * build sets VITE_ADDRESS_CHANGE=true.
 */
export function isAddressChangeEnabled(): boolean {
  return import.meta.env.VITE_ADDRESS_CHANGE === 'true';
}
